import { useEffect } from "react";
import type { ShopOrderView, ShopView } from "@frontier/content/shop";
import type { GamePlatform } from "./platform-launch";
import { ResourceSymbol } from "./ResourceSymbol";
import { shopOrderMessage } from "./shop-order-status";
import { credits } from "./ui-labels";
import { useDialogFocus } from "./use-dialog-focus";
import { shopCountdown, useShopClock } from "./shop-clock";
import "./station-pages.css";
import "./commerce.css";

export function ShopPanel({
  view,
  receivedAt,
  order,
  platform,
  pending,
  busy,
  onReload,
  onExpire,
  onBuy,
  onCheck,
  onCancelOrder,
  onClose,
}: {
  view: ShopView | null;
  receivedAt: number | null;
  order: ShopOrderView | null;
  platform: GamePlatform;
  pending: boolean;
  busy: boolean;
  onReload: () => Promise<void>;
  onExpire: () => Promise<void>;
  onBuy: (sku?: ShopView["items"][number]["sku"]) => Promise<void>;
  onCheck: () => Promise<void>;
  onCancelOrder: () => Promise<void>;
  onClose: () => void;
}) {
  const panel = useDialogFocus(onClose);
  const serverNow = useShopClock(view, receivedAt, onExpire);
  const expired =
    view?.items.some(
      (item) =>
        item.endsAt !== null && serverNow !== null && serverNow >= item.endsAt,
    ) ?? false;
  useEffect(() => {
    void onReload();
  }, [onReload]);
  return (
    <div className="commerce-overlay">
      <section
        ref={panel}
        tabIndex={-1}
        className="currency-shop"
        role="dialog"
        aria-modal="true"
        aria-label="Пополнение ресурсов"
      >
        <header className="shop-heading">
          <div>
            <small className="station-page-kicker">СНАБЖЕНИЕ ФРОНТИРА</small>
            <h2>Пополнение ресурсов</h2>
          </div>
          <button
            className="shop-close"
            onClick={onClose}
            aria-label="Закрыть магазин"
          >
            ×
          </button>
        </header>
        <div className="shop-content">
          {!pending && order && order.state !== "created" && (
            <p className="shop-status" role="status">{shopOrderMessage(order)}</p>
          )}
          {platform.kind !== "vkgames" ? (
            <p className="shop-status">Для оплаты открой игру во ВКонтакте.</p>
          ) : (
            view &&
            !view.enabled && (
              <p className="shop-status">Покупки временно недоступны.</p>
            )
          )}
          {pending && (
            <div className="shop-pending">
              <div>
                <span className="station-page-kicker">
                  НЕЗАВЕРШЁННАЯ ПОКУПКА
                </span>
                <p>{order ? order.quote.title : "Статус заказа неизвестен."}</p>
                {order && order.state !== "created" && <p role="status">Статус заказа подтверждён. Обновляем баланс…</p>}
              </div>
              <div className="shop-pending-actions">
                <button
                  className="station-button station-button-gold"
                  disabled={busy}
                  onClick={() => void onCheck()}
                >
                  Проверить оплату
                </button>
                {!order && (
                  <button
                    className="station-button"
                    disabled={busy}
                    onClick={() => void onBuy()}
                  >
                    Продолжить оплату
                  </button>
                )}
                {order?.state === "created" && !order.paymentStarted && (
                  <button
                    className="station-button"
                    disabled={busy}
                    onClick={() => void onBuy()}
                  >
                    Продолжить оплату
                  </button>
                )}
                {order?.state === "created" && (
                  <button
                    className="station-button"
                    disabled={busy}
                    onClick={() => void onCancelOrder()}
                  >
                    Отменить заказ
                  </button>
                )}
              </div>
            </div>
          )}
          {!view ? (
            <button className="station-button" onClick={() => void onReload()}>
              Загрузить наборы
            </button>
          ) : (
            <div className="shop-items">
              {view.items.map((item, index) => (
                <article
                  className={`shop-pack ${item.once ? "shop-pack-once" : ""}`}
                  key={item.sku}
                >
                  <div className="shop-pack-label">
                    <span>НАБОР {String(index + 1).padStart(2, "0")}</span>
                    {item.once && <b>АКЦИЯ</b>}
                  </div>
                  <div className="shop-pack-bonuses">
                    {(item.bonusCreditsPercent ?? 0) > 0 && (
                      <span>+{item.bonusCreditsPercent}% КР</span>
                    )}
                    {(item.bonusComponentsPercent ?? 0) > 0 && (
                      <span>+{item.bonusComponentsPercent}% КОМП.</span>
                    )}
                  </div>
                  <div className="shop-pack-art">
                    <ResourceSymbol kind="credits" />
                    <ResourceSymbol kind="components" />
                  </div>
                  <h3>
                    {credits(item.credits)} <small>КР</small>
                  </h3>
                  <strong>
                    + {item.components.toLocaleString("ru-RU")}{" "}
                    <small>компонентов</small>
                  </strong>
                  <div className="shop-pack-description">
                    {item.once && (
                      <small>
                        {item.offerId
                          ? "1 покупка за акцию"
                          : "Один раз на пилота"}
                      </small>
                    )}
                    {item.endsAt !== null && (
                      <strong
                        className="shop-offer-clock"
                        role="timer"
                        aria-label="До конца акции"
                      >
                        {serverNow === null
                          ? "…"
                          : serverNow >= item.endsAt
                            ? "Акция завершена"
                            : shopCountdown(item.endsAt, serverNow)}
                      </strong>
                    )}
                    {item.reason && <p>{item.reason}</p>}
                  </div>
                  <button
                    className="station-button station-button-gold"
                    disabled={
                      busy ||
                      pending ||
                      !view.enabled ||
                      !view.eligible ||
                      !item.available ||
                      serverNow === null ||
                      serverNow < item.startsAt ||
                      (item.endsAt !== null && serverNow >= item.endsAt) ||
                      platform.kind !== "vkgames"
                    }
                    onClick={() => void onBuy(item.sku)}
                  >
                    {item.votes}{" "}
                    {item.votes === 1
                      ? "голос"
                      : item.votes < 5
                        ? "голоса"
                        : "голосов"}
                  </button>
                </article>
              ))}
            </div>
          )}
          {expired && (
            <button
              className="station-button shop-refresh"
              onClick={() => void onReload()}
            >
              Обновить наборы
            </button>
          )}
          <p className="shop-footnote">
            При возврате оплаты ресурсы списываются, возможен отрицательный
            баланс.
          </p>
        </div>
      </section>
    </div>
  );
}
